// @flow
import classNames from 'classnames/bind';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
import { FormattedMessage, intlShape } from 'react-intl';
import logoApple from '../../../../assets/img/custom-products/iphone/apple-logo.png';
import {CommaFormat} from '../../../../elements/Price/Price';
import environment from '../../../../environment';
import FeatureSelect from '../../components/FeatureSelect/FeatureSelect';
import ProductBase from '../../components/ProductBase/ProductBase';

import coverCOImg from '../../../../assets/img/home_meta_CO_cover.png';
import coverPEImg from '../../../../assets/img/home_meta_PE_cover.png';
import coverUSImg from '../../../../assets/img/home_meta_US_cover.png';

import sgWhite from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/white.jpeg';
import sgGinger from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/ginger.jpeg';
import sgPinkCitrus from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/pink-citrus.jpeg';
import sgCyprusGreen from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/cyprus-green.jpeg';
import sgKumquat from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/kumquat.jpeg';
import sgBlack from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/black.jpeg';
import sgRed from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/red.jpeg';
import sgPlum from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/plum.jpeg';
import sgDeepNavy from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/deep-navy.jpeg';
import sgNorthernBlue from '../../../../assets/img/custom-products/apple-watch/space-gray-aluminum-case/northern-blue.jpeg';

import saWhite from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/white.jpeg';
import saGinger from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/ginger.jpeg';
import saPinkCitrus from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/pink-citrus.jpeg';
import saCyprusGreen from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/cyprus-green.jpeg';
import saKumquat from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/kumquat.jpeg';
import saBlack from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/black.jpeg';
import saRed from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/red.jpeg';
import saPlum from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/plum.jpeg';
import saDeepNavy from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/deep-navy.jpeg';
import saNorthernBlue from '../../../../assets/img/custom-products/apple-watch/silver-aluminum-case/northern-blue.jpeg';

import featureStyle from '../../components/FeatureSelect/FeatureSelect.scss';
import style from './AppleWatch.scss';

import type {LocalValuesState} from '../../../local-values/duck';
import type {LocaleState} from '../../../locale/duck';

type AppleWatchProduct = {
  name: string;
  title: string;
  description: string;
  model: string;
  images: { [key: string]: string };
  colors: string[];
  prices: { [key: string]: string };
  sizeConnectivity: { size: string; connectivity: string }[];
};

type Props = {
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +location: Location;
};

type State = {
  color: string;
  image: string;
  size: string;
  connectivity: string;
};

const countriesImages = {
  US: coverUSImg,
  PE: coverPEImg,
  CO: coverCOImg,
};

const appleWatches: { [key: string]: AppleWatchProduct } = {
  'apple-watch-6-space-gray-aluminum-case': {
    name: 'Apple Watch 6 Space Gray Aluminum Case with Solo Loop',
    model: 'Space Gray Aluminum Case with Solo Loop',
    title: 'customProduct.appleWatch6.title',
    description: 'customProduct.appleWatch6.description',
    images: {
      'white': sgWhite,
      'ginger': sgGinger,
      'pink-citrus': sgPinkCitrus,
      'cyprus-green': sgCyprusGreen,
      'kumquat': sgKumquat,
      'black': sgBlack,
      'red': sgRed,
      'plum': sgPlum,
      'deep-navy': sgDeepNavy,
      'northern-blue': sgNorthernBlue,
    },
    colors: ['white', 'ginger', 'pink-citrus', 'cyprus-green', 'kumquat', 'black', 
      'red', 'plum', 'deep-navy', 'northern-blue'],
    prices: {
      '40mm-gps': '399.00',
      '40mm-gps-cellular': '499.00',
      '44mm-gps': '429.00',
      '44mm-gps-cellular': '529.00',
    },
    sizeConnectivity: [
      {
        size: '40mm',
        connectivity: 'gps',
      },
      {
        size: '40mm',
        connectivity: 'gps-cellular',
      },
      {
        size: '44mm',
        connectivity: 'gps',
      },
      {
        size: '44mm',
        connectivity: 'gps-cellular',
      },
    ],
  },
  'apple-watch-6-silver-aluminum-case': {
    name: 'Apple Watch 6 Silver Aluminum Case with Solo Loop',
    model: 'Silver Aluminum Case with Solo Loop',
    title: 'customProduct.appleWatch6.title',
    description: 'customProduct.appleWatch6.description',
    images: {
      'white': saWhite,
      'ginger': saGinger,
      'pink-citrus': saPinkCitrus,
      'cyprus-green': saCyprusGreen,
      'kumquat': saKumquat,
      'black': saBlack,
      'red': saRed,
      'plum': saPlum,
      'deep-navy': saDeepNavy,
      'northern-blue': saNorthernBlue,
    },
    colors: ['white', 'ginger', 'pink-citrus', 'cyprus-green', 'kumquat', 'black',
      'red', 'plum', 'deep-navy', 'northern-blue'],
    prices: {
      '40mm-gps': '399.00',
      '40mm-gps-cellular': '499.00',
      '44mm-gps': '429.00',
      '44mm-gps-cellular': '529.00',
    },
    sizeConnectivity: [
      {
        size: '40mm',
        connectivity: 'gps',
      },
      {
        size: '40mm',
        connectivity: 'gps-cellular',
      },
      {
        size: '44mm',
        connectivity: 'gps',
      },
      {
        size: '44mm',
        connectivity: 'gps-cellular',
      },
    ],
  },
}

export default class AppleWatch extends Component<Props, State> {
  appleWatch: AppleWatchProduct;
  pathname: string;
  state = {
    color: '',
    image: '',
    size: '',
    connectivity: '',
  };

  constructor(props: Props) {
    super(props);
    const pathname = props.location.pathname.split('/');
    this.pathname = pathname[2];
    this.appleWatch = appleWatches[this.pathname];
  }

  componentDidMount() {
    this.setState({
      color: this.appleWatch.colors[0],
      image: this.appleWatch.images[Object.keys(this.appleWatch.images)[0]],
      size: this.appleWatch.sizeConnectivity[0].size,
      connectivity: this.appleWatch.sizeConnectivity[0].connectivity,
    });
  }


  buildUrl = () => {
    const { color } = this.state;
  };

  handleChangeColor = (color: string) => {
    this.setState({
      color,
      image: this.appleWatch.images[color],
    });
  };

  handleChangeSizeConnectivity = (size: string, connectivity: string) => {
    this.setState({ size, connectivity });
  };
  
  buildUrl = (color: string, size: string, connectivity: string) => {
    const conn = connectivity === 'gps-cellular' ? 'cellular' : 'gps';
    if (this.appleWatch.model === 'Space Gray Aluminum Case with Solo Loop') {
      return `https://www.apple.com/shop/buy-watch/apple-watch/${size}-${conn}-space-gray-aluminum-${color}-solo-loop`;
    }
    if (this.appleWatch.model === 'Silver Aluminum Case with Solo Loop') {
      return `https://www.apple.com/shop/buy-watch/apple-watch/${size}-${conn}-silver-aluminum-${color}-solo-loop`;
    }
    return '';
  };
  
  render() {
    const { locale } = this.props;
    const { color, image, size, connectivity } = this.state;
    
    const onlineStore = {
      id: 'ST08333814',
      logo: 'https://res.cloudinary.com/qempo/image/upload/v1551903426/static/stores/Apple.png',
      name: 'Apple',
      slug: 'apple',
      type: 'online - store',
      url: 'https://www.apple.com/',
    };

    const title = this.context.intl.formatMessage({ id: this.appleWatch.title });
    const description = this.context.intl.formatMessage({ id: this.appleWatch.description });
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const coverImg = countriesImages[locale.domainCountry || 'US'];

    const categoryId = 19;
    
    const priceKey = `${size}-${connectivity}`;
    
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:image', content: `${baseUrl}${coverImg}` },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        <ProductBase name={this.appleWatch.name}
                     image={image}
                     price={this.appleWatch.prices[priceKey]}
                     store={{ image: logoApple, country: 'USA' }}
                     url={this.buildUrl(color, size, connectivity)}
                     color={color}
                     onlineStore={onlineStore}
                     categoryId={categoryId}
                     additionalDetail={`Size: ${size}\nConnectivity: ${connectivity}`}
          /*preferredImportationMethod="lite"*/
        >
          <div className={style.selectCapacity}>
            <p>{`${this.context.intl.formatMessage({ id: 'customProduct.iphone.capacity.title.one' })} `}
              {this.appleWatch.name}
              {` ${this.context.intl.formatMessage({ id: 'customProduct.iphone.capacity.title.two' })}:`}</p>
            <FeatureSelect>
              {this.appleWatch.sizeConnectivity.map((sc, i) => (
                <article key={`sizeConnectivity-${i}`}
                         className={classNames({ [featureStyle.active]: priceKey === `${sc.size}-${sc.connectivity}` })}
                         onClick={() => this.handleChangeSizeConnectivity(sc.size, sc.connectivity)}>
                  <p className={style.capacity}>{sc.size}</p>
                  <p className={style.price}>
                    {sc.connectivity} {`$ ${CommaFormat({ price: this.appleWatch.prices[`${sc.size}-${sc.connectivity}`] })}`}
                  </p>
                </article>
              ))}
            </FeatureSelect>
          </div>
          <div className={style.selectColor}>
            <FormattedMessage id="customProduct.iphone.color.title">{
              text => <p>{text}</p>
            }</FormattedMessage>
            {/* color selector*/}
            <FeatureSelect>
              {this.appleWatch.colors.map((c: string, i: number) => (
                <article key={i} className={classNames({ [featureStyle.active]: c === color })}
                         onClick={() => this.handleChangeColor(c)}>
                  <div className={classNames(style.circle, style[c])}/>
                  <p>{c.replace('-', ' ')}</p>
                </article>))}
            </FeatureSelect>
          </div>
        </ProductBase>
      </Fragment>
    );
  }
}

AppleWatch.propTypes = {
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  location: PropTypes.object,
};

AppleWatch.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/custom-products/AppleWatch/components/AppleWatch.jsx